/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {AlertIndicatorValues} from './AssetAlertIndicators';
import {HealthIndicatorValues} from './AssetHealthIndicators';
import {AssetSummaryCardBody} from './AssetSummaryCardBody';
import {AssetSummaryCardDiagnostics} from './AssetSummaryCardDiagnostics';
import {AssetSummaryCardHeader} from './AssetSummaryCardHeader';
import {AssetSummaryCardStatusProps} from './AssetSummaryCardStatus';
import {VehicleFuelType} from './vehicleTypeIndicatorConfig';

export interface AssetSummaryCardProps {
  assetType: string;
  assetName: string;
  alias?: string;
  onAssetNameClick?: () => void;
  status: AssetSummaryCardStatusProps;
  location?: string;
  group?: string;
  route?: string;
  driver?: string;
  onDriverClick?: () => void;
  vehicleType: VehicleFuelType;
  healthValues: HealthIndicatorValues;
  alertValues: AlertIndicatorValues;
}

const useStyles = makeStyles(() => ({
  card : {
    display       : 'flex',
    width         : '100%',
    // without this, flex children whose own content doesn't wrap (e.g.
    // Header Right's date/time) can refuse to shrink below their natural
    // content width and force this card - and its host sidesheet - wider
    // than the available space; overflow is a defensive backstop for
    // whatever can't be helped by shrinking (e.g. a very long state label) ...
    minWidth      : 0,
    maxWidth      : '100%',
    overflow      : 'hidden',
    boxSizing     : 'border-box',
    flexDirection : 'column',
    alignItems    : 'flex-start',
    gap           : '24px',
    padding       : '16px 24px'
    // no border of its own - per the Figma "Asset List View" list-container
    // frame, adjacent cards are separated by a single 1px line owned by the
    // list item that wraps this component (see AssetCard.tsx), not by each
    // card individually - having both doubles the line at every boundary ...
  }
}));

/**
 * AssetSummaryCard is the ION-7392 Asset Summary Card for the DLS current
 * theme: a Header (asset identity + status), Body (location/group/driver),
 * and Diagnostic Summary (health/alerts), each of which independently
 * omits its own empty rows/sections without leaving gaps, so the card's
 * overall height always tracks whatever content is actually present.
 *
 * The card itself has no fixed width - it fills its container at 100%
 * (with `minWidth: 0`/`overflow: hidden` so its own content can never force
 * the container, e.g. a narrow sidesheet, wider than that), which is what
 * makes it contract to the available width on mobile while using the exact
 * same component on desktop and mobile per ION-7400. It also has no
 * background color of its own - Hover/Selected states (per Figma's
 * `SidePanel/AssetCard/Hover` and `/Selected` variants) are provided by
 * whatever wraps this component (e.g. the list item in `AssetCard.tsx`,
 * which already implements a hover/selected background), so this card
 * paints transparently over it rather than obscuring it. Tooltips
 * throughout already disable themselves on mobile internally (`Tooltip`'s
 * own `useIsPhone` gate), so that needs no separate handling here either.
 *
 * ```typescript
 *
 *   import {AssetSummaryCard} from
 *     '@ion/dls/src/components/assetcard/current/AssetSummaryCard';
 *   ...
 *
 *   <AssetSummaryCard assetType={AssetType.Vehicle}
 *     assetName="Vehicle 13 Vision"
 *     alias="004013982 - HUMAN SERVICES - 21-0763"
 *     onAssetNameClick={onAssetNameClick}
 *     status={{
 *       stateLabel: 'Moving (no Ignition)',
 *       stateTextColor: dlsColor('paleOrange'),
 *       stateBackgroundColor: 'rgba(225, 114, 0, 0.1)',
 *       dateTime: '12/16/2025 - 11:08 AM CST',
 *       speed: '11 MPH'
 *     }}
 *     location="13505 Zachary Taylor Dr, Manassas, Virginia"
 *     group="STREETS & STREETS ROADSIDE VEGETATION"
 *     route="Route Name"
 *     driver="Thomas Thumb"
 *     onDriverClick={onDriverClick}
 *     vehicleType="gas"
 *     healthValues={{seatbelt: {value: 'Buckled'}, assetBattery: {value: '100%'}}}
 *     alertValues={{}} />
 *
 * ```
 *
 */
// this card renders once per visible row in the tracking list's virtualized
// react-window list - memoized so a row's own hover/selected re-renders
// (which don't change any of these props) don't also re-render the whole
// Header/Body/Diagnostics subtree beneath it ...
export const AssetSummaryCard = memo(({
  assetType,
  assetName,
  alias,
  onAssetNameClick,
  status,
  location,
  group,
  route,
  driver,
  onDriverClick,
  vehicleType,
  healthValues,
  alertValues
}: AssetSummaryCardProps) => {
  const classes = useStyles();
  return (
    <div className={classes.card}>
      <AssetSummaryCardHeader assetType={assetType}
        assetName={assetName}
        alias={alias}
        onAssetNameClick={onAssetNameClick}
        status={status} />
      <AssetSummaryCardBody location={location}
        group={group}
        route={route}
        driver={driver}
        onDriverClick={onDriverClick} />
      <AssetSummaryCardDiagnostics vehicleType={vehicleType}
        healthValues={healthValues}
        alertValues={alertValues} />
    </div>
  );
});

export default AssetSummaryCard;
